import React from 'react';

import {
  DateField,
  FunctionField,
  TextField,
  useRecordContext,
} from 'react-admin';

import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Box,
  Divider,
} from '@mui/material';

import { isEmpty, isString } from 'lodash';

import { Alpha3Code, getName } from 'i18n-iso-countries';

import { SanctionListMatches } from '../../utilities/schemas/sanctionListMatches';

import SanctionTitle from './SanctionTitle';

import languages from '../../constants/languages';

const getAddress = ({
  streetAddress,
  additionalAddress,
  city,
  country,
  description,
  postcode,
  state,
}: {
  streetAddress?: string;
  additionalAddress?: string;
  postcode?: string;
  city?: string;
  state?: string;
  country?: Alpha3Code;
  description?: string;
}) => {
  if (description && !isEmpty(description)) {
    return description;
  }

  const address = [
    streetAddress,
    additionalAddress,
    postcode,
    city,
    state,
    country ? getName(country, languages.EN) : null,
  ].filter((item) => item && isString(item) && !isEmpty(item));

  return address.join(', ');
};

const PepAboutIndividual = () => {
  const record = useRecordContext<SanctionListMatches['potentialMatches'][number]>();

  if (!record) {
    return null;
  }

  return (
    <Grid size={12}>
      <Card variant="outlined" sx={{ margin: 0 }}>
        <CardHeader title="About" />
        <Divider />
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Box>
                <SanctionTitle>Name</SanctionTitle>
                <TextField source="fullName" emptyText="-" />
              </Box>
            </Grid>
            <Grid size={12}>
              <Box>
                <SanctionTitle>Registration number</SanctionTitle>
                <DateField source="registrationNumber" emptyText="-" />
              </Box>
            </Grid>
            <Grid size={12}>
              <Box>
                <SanctionTitle>Registration country</SanctionTitle>
                <DateField source="registrationCountry" emptyText="-" />
              </Box>
            </Grid>
            <Grid size={12}>
              <Box>
                <SanctionTitle>Other names</SanctionTitle>
                <FunctionField
                  sx={{ whiteSpace: 'pre' }}
                  source="otherNames"
                  render={(item: SanctionListMatches['potentialMatches'][number]) => {
                    if (isEmpty(item.otherNames)) return '-';
                    return record.otherNames
                      .map((name) => name.fullName)
                      .join('\n');
                  }}
                />
              </Box>
            </Grid>
            <Grid size={12}>
              <Box>
                <SanctionTitle>Address</SanctionTitle>
                <FunctionField
                  sx={{ whiteSpace: 'pre' }}
                  source="address"
                  render={(item: SanctionListMatches['potentialMatches'][number]) => {
                    if (!item.address) return '-';
                    return getAddress(item.address);
                  }}
                />
              </Box>
            </Grid>
            <Grid size={12}>
              <Box>
                <SanctionTitle>Other addresses</SanctionTitle>
                <FunctionField
                  sx={{ whiteSpace: 'pre' }}
                  source="otherAddresses"
                  render={(item: SanctionListMatches['potentialMatches'][number]) => {
                    if (isEmpty(item.otherAddresses)) return '-';
                    return record.otherAddresses
                      .map((otherAddress) => getAddress(otherAddress))
                      .join('\n');
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default PepAboutIndividual;
